import {
  bindActionCreators,
  combineReducers,
  legacy_createStore as createStore,
  applyMiddleware,
  compose,
} from "redux";
import { thunk } from "redux-thunk";
import { persistStore, persistReducer } from "redux-persist";
import storageLocal from "redux-persist/lib/storage";
import storageSession from "redux-persist/lib/storage/session";

import { reducers as additionalInfoReducers } from "../modules/additional-info";
import { reducers as alertsReducers } from "../modules/alerts";
import { reducers as analyticsReducers } from "../modules/analytics";
import { reducers as appReducers } from "../modules/app";
import { reducers as blogIndexReducers } from "../modules/blog-index";
import { reducers as categoriesReducers } from "../modules/categories";
import { reducers as compareReducers } from "../modules/compare";
import { reducers as emailOptInReducers } from "../modules/email-opt-in";
import { reducers as listingReducers } from "../modules/listing";
import { reducers as locationReducers } from "../modules/location";
import { reducers as loginReducers } from "../modules/login";
import { reducers as modalFilesReducers } from "../modules/modal-files";
import { reducers as modalFindReducers } from "../modules/modal-find";
import {
  actions as modalReauthorizeActions,
  reducers as modalReauthorizeReducers,
} from "../modules/modal-reauthorize";
import { reducers as modalSellSheetReducers } from "../modules/modal-sell-sheet";
import { reducers as recaptchaReducers } from "../modules/recaptcha";
import { reducers as searchReducers } from "../modules/search";
import { reducers as selectFilesReducers } from "../modules/select-files/SelectFiles.redux";
import { reducers as userReducers } from "../modules/user";
import { reducers as watchReducers } from "../modules/watch";

import { HOST_ENV, SITE_AUTH } from "../constants";

const initialState = {};

/**
 * Configures how redux state is persisted to session storage
 */
const persistConfig = {
  key: "public",
  storage: storageSession,
  whitelist: [
    "additionalInfo",
    "alerts",
    "blogCategories",
    "categories",
    "categoriesCount",
    "categoriesExpires",
    "comparedListings",
    "emailOptIn",
    "featureSupport",
    "locations",
    "searchCurrentMeta",
    "searchResults",
  ],
};

/**
 * Analytics state persist to local storage so that
 * it sticks around longer than a single session
 */
const analyticsPersistConfig = {
  key: "hu_analytics",
  storage: storageLocal,
  whitelist: ["attribution"],
};
const combinedAnalytics = combineReducers({ ...analyticsReducers });

/**
 * Auth and user state persist to local storage so that
 * it sticks around longer than a single session
 */
const authPersistConfig = {
  key: SITE_AUTH,
  storage: storageLocal,
  whitelist: ["auth", "user", "userWatchedListings"],
};
const combinedLogin = combineReducers({ ...loginReducers, ...userReducers });

/**
 * Combines reducers imported from modules
 */
const combined = combineReducers({
  analytics: persistReducer(analyticsPersistConfig, combinedAnalytics),
  login: persistReducer(authPersistConfig, combinedLogin),
  ...additionalInfoReducers,
  ...alertsReducers,
  ...appReducers,
  ...blogIndexReducers,
  ...categoriesReducers,
  ...compareReducers,
  ...emailOptInReducers,
  ...listingReducers,
  ...locationReducers,
  ...modalFilesReducers,
  ...modalFindReducers,
  ...modalReauthorizeReducers,
  ...modalSellSheetReducers,
  ...searchReducers,
  ...selectFilesReducers,
  ...recaptchaReducers,
  ...watchReducers,
});

/**
 * Creates root reducer and provides method for clearing store
 * @param {object} state
 * @param {object} action
 */
const rootReducer = (state, action) => {
  if (action.type === "CLEAR_ALL") {
    state = undefined;
  }

  return combined(state, action);
};

const persistedReducers = persistReducer(persistConfig, rootReducer);

/**
 * Applies Redux enhancers
 * * thunk
 */
const composeEnhancers =
  (HOST_ENV !== "production" && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose;
const enhancer = composeEnhancers(applyMiddleware(thunk));

/**
 * Creates Redux store
 */
const store = createStore(persistedReducers, initialState, enhancer);
export const persistor = persistStore(store);
export const globalReauthorizeActions = bindActionCreators(
  modalReauthorizeActions,
  store.dispatch
);

export default store;
